import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { authClient } from '../../lib/auth-client'
import { 
  Key, 
  Plus, 
  Trash2, 
  Copy, 
  Eye, 
  EyeOff,
  AlertCircle,
  CheckCircle2,
  X
} from 'lucide-react'

interface ApiKey {
  id: string
  name: string
  key: string
  prefix: string
  createdAt: string
  lastUsedAt: string | null
}

export function ApiKeysPage() {
  const { data: session } = authClient.useSession()
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKey, setNewKey] = useState<string | null>(null)
  const [showKeyId, setShowKeyId] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchKeys()
  }, [])

  const fetchKeys = async () => {
    try {
      const res = await fetch('/api/v1/keys', {
        headers: { 'Authorization': `Bearer ${session?.session.token}` }
      })
      if (res.ok) {
        const data = await res.json()
        setKeys(data.keys || [])
      }
    } catch (err) {
      console.error('Failed to fetch keys:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const createKey = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsCreating(true)

    try {
      const res = await fetch('/api/v1/keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.session.token}`
        },
        body: JSON.stringify({
          name: newKeyName || 'Default',
          scopes: ['*'],
        }),
      })

      if (res.ok) {
        const data = await res.json()
        setNewKey(data.key)
        fetchKeys()
        setNewKeyName('')
      } else {
        const err = await res.json()
        setError(err.message || 'Failed to create key')
      }
    } catch (err) {
      setError('Something went wrong')
    } finally {
      setIsCreating(false)
    }
  }

  const revokeKey = async (keyId: string) => {
    if (!confirm('Are you sure? This will immediately revoke this API key.')) return

    try {
      const res = await fetch(`/api/v1/keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session?.session.token}` }
      })

      if (res.ok) {
        fetchKeys()
      }
    } catch (err) {
      console.error('Failed to revoke key:', err)
    }
  }

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <>
      <Helmet>
        <title>API Keys | Twenty</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight mb-2">API Keys</h1>
            <p className="text-muted-foreground">
              Manage keys for accessing the infrastructure API
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowCreateModal(true)
              setNewKey(null)
              setNewKeyName('')
              setError('')
            }}
            className="btn-factory"
          >
            <Plus className="w-4 h-4" />
            Create key
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-pulse text-muted-foreground" aria-live="polite">Loading keys…</div>
          </div>
        ) : keys.length === 0 ? (
          <div className="card-factory p-12 text-center">
            <div className="w-12 h-12 rounded-sm bg-secondary flex items-center justify-center mx-auto mb-4">
              <Key className="w-6 h-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">No API keys yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first key to start using the API
            </p>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(true)}
                    className="btn-factory"
                  >
              <Plus className="w-4 h-4" />
              Create key
            </button>
          </div>
        ) : (
          <div className="card-factory overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-secondary/50">
                  <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Name</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Key</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Created</th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-muted-foreground">Last used</th>
                  <th className="text-right py-3 px-6 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id} className="border-b border-border last:border-0">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{key.name}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono bg-secondary px-2 py-1 rounded-sm">
                          {showKeyId === key.id ? key.key : `${key.prefix}••••••••`}
                        </code>
                        <button
                          type="button"
                          onClick={() => setShowKeyId(showKeyId === key.id ? null : key.id)}
                          className="p-1 hover:bg-secondary rounded-sm"
                          aria-label={showKeyId === key.id ? 'Hide key' : 'Show key'}
                        >
                          {showKeyId === key.id ? (
                            <EyeOff className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(key.key, key.id)}
                          className="p-1 hover:bg-secondary rounded-sm"
                          aria-label="Copy key"
                        >
                          {copiedId === key.id ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-muted-foreground">
                        {new Date(key.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm text-muted-foreground">
                        {key.lastUsedAt
                          ? new Date(key.lastUsedAt).toLocaleDateString()
                          : 'Never'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <button
                        type="button"
                        onClick={() => revokeKey(key.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                        title="Revoke key"
                        aria-label="Revoke key"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Key Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card-factory p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium">
                  {newKey ? 'Key Created' : 'Create API Key'}
                </h3>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCreateModal(false)
                          setNewKey(null)
                        }}
                        className="p-1 hover:bg-secondary rounded-sm"
                        aria-label="Close modal"
                      >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {newKey ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-sm">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Success!</span>
                    </div>
                    <p className="text-sm text-green-600 mb-4">
                      Copy this key now. You won't be able to see it again.
                    </p>
                    <div className="flex items-center gap-2 p-3 bg-white rounded-sm border border-green-200">
                      <code className="flex-1 text-sm font-mono break-all">
                        {newKey}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(newKey, 'new')}
                        className="p-2 hover:bg-green-50 rounded-sm"
                        aria-label="Copy key"
                      >
                        {copiedId === 'new' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setShowCreateModal(false)
                      setNewKey(null)
                    }}
                    className="btn-factory w-full"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={createKey} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Key Name</label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      className="input-factory"
                      placeholder="Production, Development, etc."
                      autoFocus
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="btn-factory-outline flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isCreating}
                      className="btn-factory flex-1"
                    >
                      {isCreating ? 'Creating…' : 'Create key'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
